import keys from '@debbie/common/dist/cache/keys.js';
import { DepositShareType, getBillingVoucherIdsFromDepositShare, getDepositShareSums, } from '@debbie/common/dist/economy/billing/organize.js';
import { VoucherCategory, } from '@debbie/common/dist/economy/vouchers/index.js';
import { Site } from '@debbie/common/dist/index.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import { objectBy } from '@debbie/common/dist/util/index.js';
import * as billingVouchersAPI from '@debbie/cortex/dist/api/billing-vouchers.js';
import * as billingAPI from '@debbie/cortex/dist/api/billing.js';
import { billingsEntity } from '@debbie/cortex/dist/billings/index.js';
import q from '@debbie/cortex/dist/query.js';
import React from 'react';
import { useSelector } from 'react-redux';
import THEME from '../../theme.js';
import { errorNotification } from '../../utils/errors.js';
import { powerTableEconomicalValues } from '../economy/billing-table-utils.js';
import PaymentTypeComponent from '../economy/case-vouchers/PaymentType.js';
import DepositShareComponent from '../economy/DepositShare.js';
import { Badge, Button, ButtonGroup, CasePreview, Modal, Money, PlaceholderLoader, Timestamp, } from '../index.js';
import PowerTable, { FilterType } from '../PowerTable.js';
var BillingRowType;
(function (BillingRowType) {
    BillingRowType["Deposit"] = "Deposit";
})(BillingRowType || (BillingRowType = {}));
const UIPreview = (props) => {
    const { billing } = props;
    const lang = useSelector((s) => s.lang);
    const site = useSelector((s) => s.site);
    const [selected, setSelected] = React.useState([]);
    const [depositSharePreview, setDepositSharePreview] = React.useState(null);
    let billingRows = billing && transformBillingToRows(billing, lang);
    const createBillingCollectionMutation = useMutation(async () => {
        if (props.billingSelector) {
            const billingVoucherIds = selected.length
                ? {
                    billingVoucherIds: getSelectedBillingVoucherIds(billingRows, selected, billing),
                }
                : {};
            await billingAPI.createBillingCollection({
                newBillings: [
                    {
                        ...props.billingSelector,
                        ...billingVoucherIds,
                    },
                ],
            });
        }
    }, {
        onSuccess: () => {
            setSelected([]);
            q.invalidate(keys.write.create(billingsEntity));
            if (props.closeModal) {
                props.closeModal();
            }
        },
        onError: (err) => {
            errorNotification(lang, err, 'create', lang.errorEntities.billingCollection);
        },
    });
    const deleteBillingVouchersMutation = useMutation(async () => {
        await billingVouchersAPI.del(selected.length === 0
            ? billing.billingVouchers.map((billingVoucher) => billingVoucher.billingVoucherId)
            : getSelectedBillingVoucherIds(billingRows, selected, billing));
        setSelected([]);
        q.invalidate(keys.write.modify(billingsEntity, billing.billingId));
        if (props.closeModal) {
            props.closeModal();
        }
    }, {
        onError: (err) => {
            errorNotification(lang, err, 'delete', lang.errorEntities.billing);
        },
    });
    if (props.highlightCaseId) {
        billingRows = billingRows.sort((a, b) => {
            return ((b.caseId === props.highlightCaseId ? 1 : 0) -
                (a.caseId === props.highlightCaseId ? 1 : 0));
        });
    }
    return (React.createElement("div", null, billingRows ? (React.createElement(React.Fragment, null,
        React.createElement("div", { style: { fontSize: '0.8em' } }, React.createElement(PowerTable, { data: billingRows, genKey: (row) => row.key, searchable: true, toSearchStr: (br) => `${br.text ?? ''} ${br.caseSequentialId ?? ''} ${br.customerReferenceId ?? ''} ${br.sequentialId ?? ''} ${br.type}`, selectable: true, selectKey: (row) => row.key, selected: selected, defaultSortKey: props.highlightCaseId ? undefined : 'date', onSelect: setSelected, onClick: (row) => {
                if (row.key.indexOf(BillingRowType.Deposit) !== -1) {
                    const depositShare = billing.depositShare.find((depositShare) => row.key.indexOf(depositShare.deposit?.caseVoucherId || '') !== -1 && row.key.indexOf(depositShare.type) !== -1);
                    if (depositShare) {
                        setDepositSharePreview(depositShare);
                    }
                }
            }, pagination: {
                type: 'pages',
                pageSize: 50,
            }, values: {
                sequentialId: {
                    toCmp: (id) => id || 0,
                    label: lang.billing.billingSequentialId,
                    map: (id, row) => (React.createElement(React.Fragment, null,
                        props.highlightCaseId &&
                            row.caseId &&
                            props.highlightCaseId === row.caseId ? (React.createElement("span", { style: {
                                width: '8px',
                                height: '8px',
                                backgroundColor: THEME.COLOR_ACCENT,
                                display: 'inline-block',
                                position: 'absolute',
                                transform: 'translate(-13px, 3px)',
                                borderRadius: '100%',
                            } })) : null,
                        id)),
                },
                caseSequentialId: {
                    label: lang.billing.case,
                    // @ts-ignore
                    map: (caseSequentialId, br) => br.caseId ? (React.createElement(CasePreview, { caseId: br.caseId, seqId: caseSequentialId ?? undefined })) : undefined,
                },
                date: {
                    label: lang.billing.date,
                    map: (date) => {
                        return date && React.createElement(Timestamp, { date: date });
                    },
                    align: 'right',
                    filter: {
                        type: FilterType.DateRange,
                    },
                },
                type: {
                    label: lang.billing.type,
                    toCmp: (type) => type,
                    map: (type, row) => {
                        return (React.createElement("div", { style: {
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }, title: type },
                            React.createElement("b", null, type),
                            ' ',
                            row.depositShareType === DepositShareType.Deposit && (React.createElement("span", null,
                                row.paymentType && (React.createElement(PaymentTypeComponent, { paymentType: row.paymentType })),
                                ' ',
                                row.depositState &&
                                    lang.caseVoucherStates[row.depositState]))));
                    },
                    filter: {
                        type: FilterType.Category,
                        categories: objectBy(billingRows.map(({ type }) => ({ label: type, type })), ({ type }) => type),
                    },
                },
                text: {
                    label: lang.billing.text,
                    // @ts-ignore
                    map: (text) => text ? (React.createElement("div", { style: {
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }, title: text }, text)) : null,
                },
                customerReferenceId: {
                    label: lang.billing.customerReferenceId,
                    // @ts-ignore
                    map: (id) => id,
                },
                deposits: {
                    label: lang.billing.deposit,
                    align: 'right',
                    toCmp: (deposits) => deposits,
                    map: (deposits) => React.createElement(Money, { amount: deposits }),
                    filter: {
                        type: FilterType.MoneyRange,
                    },
                },
                ...powerTableEconomicalValues({
                    lang,
                }),
            } })),
        depositSharePreview && (React.createElement(Modal, { maxWidth: "700px", onToggle: () => setDepositSharePreview(null) },
            React.createElement(DepositShareComponent, { depositShare: depositSharePreview, billingSelector: props.billingSelector }))),
        React.createElement("br", null),
        React.createElement(ButtonGroup, null,
            props.billingSelector && site === Site.Caseworker && (React.createElement(Button, { size: "small", loading: createBillingCollectionMutation.isLoading, onClick: () => createBillingCollectionMutation.mutate() },
                lang.billing.bill,
                ' ',
                !!selected.length && (React.createElement(Badge, { style: { color: THEME.COLOR_ACCENT }, theme: THEME.COLOR_SECONDARY }, selected.length)))),
            props.billingSelector && (React.createElement(Button, { size: "small", loading: deleteBillingVouchersMutation.isLoading, onClick: () => {
                    if (confirm(lang.billing.deleteApproval)) {
                        deleteBillingVouchersMutation.mutate();
                    }
                }, theme: "dangerous" },
                lang.billing.delete,
                ' ',
                !!selected.length && (React.createElement(Badge, { style: { color: THEME.COLOR_ACCENT }, theme: THEME.COLOR_SECONDARY }, selected.length))))))) : (React.createElement(PlaceholderLoader, null))));
};
const getSelectedBillingVoucherIds = (billingRows, selected, billing) => selected
    ? selected
        .map((key) => {
        const row = billingRows.find((row) => row.key === key);
        if (!row) {
            console.log(key);
            throw new Error('Could not find billing row');
        }
        return row.billingVoucherIds;
    })
        .reduce((acc, arr) => acc.concat(arr), [])
    : billing.billingVouchers.map((billingVoucher) => billingVoucher.billingVoucherId);
const mapBillingVoucherToRow = (lang) => (billingVoucher) => {
    const revenue = [
        VoucherCategory.COLLECTIONCOMMISSION,
        VoucherCategory.FEE,
    ].includes(billingVoucher.category)
        ? billingVoucher.amount
        : 0;
    const disbursements = billingVoucher.category === VoucherCategory.DISBURSEMENT
        ? billingVoucher.amount
        : 0;
    const vat = 
    // @ts-ignore
    (billingVoucher.vat
        ? // @ts-ignore
            billingVoucher.vat.reduce((acc, billingVoucher) => acc + billingVoucher.amount, 0)
        : 0) +
        (billingVoucher.category === VoucherCategory.VAT
            ? billingVoucher.amount
            : 0);
    return {
        key: billingVoucher.billingVoucherId,
        type: lang.economyVoucherTypeLabels[billingVoucher.voucherTypeId] || billingVoucher.label,
        date: billingVoucher.date,
        caseSequentialId: billingVoucher.info?.caseSequentialId,
        caseId: billingVoucher.caseId || null,
        customerReferenceId: billingVoucher.info?.customerReferenceId,
        text: billingVoucher.text,
        sequentialId: billingVoucher.id,
        billingVoucherIds: [billingVoucher.billingVoucherId].concat(
        // @ts-ignore
        (billingVoucher.vat || []).map((vatBillingVoucher) => vatBillingVoucher.billingVoucherId)),
        deposits: 0,
        revenue,
        disbursements,
        vat,
        balance: revenue + vat + disbursements,
        payout: 0,
        overpayments: 0,
    };
};
const transformBillingToRows = (billing, lang) => {
    let rows = [];
    // Concat deposit share
    rows = rows.concat(billing.depositShare.map((depositShare) => {
        const { collectionCommission, vat, balance, payout, overpayment, deposit, } = getDepositShareSums(depositShare);
        const billingVoucherIds = getBillingVoucherIdsFromDepositShare(depositShare);
        return {
            key: `${BillingRowType.Deposit}${depositShare.deposit?.caseVoucherId || billingVoucherIds.join(',')}${depositShare.type}`,
            type: lang.depositShareType[depositShare.type],
            date: depositShare.deposit?.date,
            sequentialId: depositShare.deposit?.id,
            caseSequentialId: depositShare.info?.caseSequentialId,
            caseId: depositShare.caseId || null,
            customerReferenceId: depositShare.info?.customerReferenceId,
            depositState: depositShare.deposit?.state,
            depositShareType: depositShare.type,
            deposits: deposit,
            revenue: collectionCommission,
            vat,
            balance,
            payout,
            disbursements: 0,
            overpayments: overpayment,
            paymentType: depositShare.deposit?.paymentType,
            billingVoucherIds: billingVoucherIds,
        };
    }));
    // Concat fees
    rows = rows.concat(billing.fees.map(mapBillingVoucherToRow(lang)));
    // Concat disbursements
    rows = rows.concat(billing.disbursements.map(mapBillingVoucherToRow(lang)));
    // Concat vat
    rows = rows.concat(billing.vat.map(mapBillingVoucherToRow(lang)));
    return rows;
};
export default UIPreview;
//# sourceMappingURL=UIPreview.js.map